import React, { useMemo, useState } from "react";
import { Card, Descriptions, Drawer, Table, Tag, Typography } from "antd";
import { MDBCol, MDBRow } from "mdbreact";
import { Text } from "react-font";
import { Button, SearchInput } from "evergreen-ui";
import { TableExport } from "tableexport";
import FireFetch from "../../Firebase/FireFetch";
import { useListVals } from "../../Firebase/databaseHooks";
import Firebase from "../../Firebase/Firebase";
import { getDatabase, ref } from "firebase/database";
import TableActionMenu from "../../Common/TableActionMenu";
import {
	getRecordId,
	getOrganizationName,
	getSubmittedById,
	getUserName,
	includesSearch,
	useMergedListVals,
} from "../../Firebase/dataCompat";

const db = getDatabase(Firebase);
const { Paragraph } = Typography;

const Feedback = () => {
	const [feedback, loading] = useMergedListVals("talmidsdb/feedback", "talmidsdb/Feedback");
	const [users] = useListVals(ref(db, "talmidsdb/users"));
	const [communities] = useListVals(ref(db, "talmidsdb/communities"));
	const [organisations] = useListVals(ref(db, "talmidsdb/organisations"));
	const [selectedFeedback, setSelectedFeedback] = useState(null);
	const [viewOpen, setViewOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");

	const getCommunityName = (id) => {
		const community = communities?.find((item) => item.id === id || item.communityID === id);
		return community?.name || "-";
	};

	const getOrganisationName = (id) => {
		return getOrganizationName(organisations, id, "-");
	};

	const rows = useMemo(() => {
		return (feedback || []).map((item, index) => {
			const id = getRecordId(item) || String(index);
			const submittedBy = getUserName(users, getSubmittedById(item));
			const community = getCommunityName(item.communityID || item.communityId);
			const organisation = getOrganisationName(item.organisationID || item.orgID || item.organizationID);
			const type = item.feedbackType || item.type || item.category || "-";
			const message = item.feedback || item.message || item.comment || item.notes || item.description || "-";
			const time = item.timeStamp || item.timestamp || item.createdAt || item.dateCreated || "-";

			return {
				key: id,
				id,
				type,
				message,
				submittedBy,
				community,
				organisation,
				time,
				_full: item,
				action: (
					<TableActionMenu
						items={[
							{
								key: "view",
								label: "View",
								onClick: () => {
									setSelectedFeedback(item);
									setViewOpen(true);
								},
							},
						]}
					/>
				),
			};
		});
	}, [feedback, users, communities, organisations]);

	const filteredRows = useMemo(
		() =>
			rows.filter((row) =>
				includesSearch(
					[row.type, row.message, row.submittedBy, row.community, row.organisation, row.time],
					searchValue
				)
			),
		[rows, searchValue]
	);

	const columns = [
		{ title: "Type", dataIndex: "type", key: "type", sorter: (a, b) => a.type.localeCompare(b.type) },
		{
			title: "Feedback",
			dataIndex: "message",
			key: "message",
			render: (value) => <Paragraph ellipsis={{ rows: 2, expandable: true }}>{value}</Paragraph>,
		},
		{ title: "Submitted By", dataIndex: "submittedBy", key: "submittedBy", sorter: (a, b) => a.submittedBy.localeCompare(b.submittedBy) },
		{ title: "Community", dataIndex: "community", key: "community", sorter: (a, b) => a.community.localeCompare(b.community) },
		{ title: "Organization", dataIndex: "organisation", key: "organisation", responsive: ["sm"] },
		{ title: "Time", dataIndex: "time", key: "time", responsive: ["sm"] },
		{ title: "Action", dataIndex: "action", key: "action" },
	];

	return (
		<div className="d-flex justify-content-center align-items-center pt-4 mt-4">
			<Drawer
				title="View Feedback"
				width={720}
				maskClosable
				className="font-Nunito"
				placement="right"
				visible={viewOpen}
				onClose={() => setViewOpen(false)}
			>
				{selectedFeedback && (
					<Descriptions column={1} bordered>
						<Descriptions.Item label="Type">
							<Tag color="blue">{selectedFeedback.feedbackType || selectedFeedback.type || selectedFeedback.category || "-"}</Tag>
						</Descriptions.Item>
						<Descriptions.Item label="Submitted By">
							{getUserName(users, getSubmittedById(selectedFeedback))}
						</Descriptions.Item>
						<Descriptions.Item label="Community">
							{getCommunityName(selectedFeedback.communityID || selectedFeedback.communityId)}
						</Descriptions.Item>
						<Descriptions.Item label="Organization">
							{getOrganisationName(selectedFeedback.organisationID || selectedFeedback.orgID || selectedFeedback.organizationID)}
						</Descriptions.Item>
						<Descriptions.Item label="Date">
							{selectedFeedback.timeStamp || selectedFeedback.timestamp || selectedFeedback.createdAt || selectedFeedback.dateCreated || "-"}
						</Descriptions.Item>
						<Descriptions.Item label="Feedback">
							{selectedFeedback.feedback || selectedFeedback.message || selectedFeedback.comment || selectedFeedback.notes || selectedFeedback.description || "-"}
						</Descriptions.Item>
					</Descriptions>
				)}
			</Drawer>
			<MDBRow className="w-100">
				<MDBCol md={12}>
					<Card className="w-100">
						<div className="d-flex flex-row justify-content-between">
							<h5 className="font-weight-bold" style={{ color: "#51195D" }}>
								<Text family="Nunito">Feedback</Text>
							</h5>
							<div className="d-flex flex-row">
								<SearchInput
									placeholder="Filter feedback"
									className="mx-1"
									value={searchValue}
									onChange={(event) => setSearchValue(event.target.value)}
								/>
								<Button
									appearance="primary"
									onClick={() => {
										FireFetch.exportXL(TableExport(document.getElementById("dataTable"), {
											filename: "Feedback",
											exportButtons: false,
											sheetname: "Feedback",
										}), filteredRows);
									}}
								>
									Print
								</Button>
							</div>
						</div>
					</Card>
					<Card>
						<Table
							id="dataTable"
							className="custom-table"
							loading={loading}
							pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ["100", "200", "300"] }}
							style={{ overflow: "auto" }}
							columns={columns}
							dataSource={filteredRows}
						/>
					</Card>
				</MDBCol>
			</MDBRow>
		</div>
	);
};

export default Feedback;
